import Text from '../../../../../../Components/Base/Text/Text';
import FormContainer from '../../../../../../Components/Form/FormContainer';
import CalendarInputField from '../../../../../../Components/Form/components/CalendarInputField';
import DateField from '../../../../../../Components/Form/components/DateField';
import DoctorSelectField from '../../../../../../Components/Form/components/DoctorSelectField';
import Input from '../../../../../../Components/Form/components/Input';
import SelectField from '../../../../../../Components/Form/components/SelectField';
import TextAreaField from '../../../../../../Components/Form/components/TextAreaField';
import TopFieldContent from '../../../../../../Components/Form/components/TopFieldContent';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { FieldNames } from '../../../../../../Services/HealthBabyPlanDataContext';
// @ts-expect-error
import styles from './FinalStepFields.module.scss';
import React from 'react';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import InputField from '../../../../../../Components/Form/components/InputField';

export const FINAL_STEP_VALIDATION_TRANSLATION_PREFIX =
	'validation.finalStepForm';

const getStringFieldValidation = (validationMessage: string) =>
	yup.string().nullable().required(validationMessage);

type ITranslations = (key: string) => string | undefined;

export const getFinalStepValidationSchema = (
	t: ITranslations,
	isEntryQuestionnaire = false,
) =>
	yup.object({
		[FieldNames.FINAL_STEP]: yup.object({
			...(isEntryQuestionnaire && {
				careDate: getStringFieldValidation(
					t(`${FINAL_STEP_VALIDATION_TRANSLATION_PREFIX}.careDate`) ?? '',
				),
			}),
			cervixScore: yup
				.object()
				.nullable()
				.test(
					'object-check',
					t(`${FINAL_STEP_VALIDATION_TRANSLATION_PREFIX}.cervixScore`) ?? '',
					score => !isEmpty(score),
				),
			identificationCode: isEntryQuestionnaire
				? yup.string().required()
				: yup.mixed().notRequired(),
			nextExaminationDate: yup.string().nullable().notRequired(),
			doctor: yup
				.object()
				.test(
					'object-check',
					t(`${FINAL_STEP_VALIDATION_TRANSLATION_PREFIX}.doctorName`) ?? '',
					({ value }) => Boolean(value),
				)
				.nullable(),
		}),
	});

interface Props {
	title?: string;
	patientId: string;
	workplaceId: string;
	dateDifference?: string;
	doctorId?: string;
	isEntryQuestionnaire?: boolean;
}

const FinalStepFields: React.FC<Props> = ({
	title = 'base.finalStep',
	patientId,
	workplaceId,
	dateDifference,
	doctorId,
	isEntryQuestionnaire = false,
}) => {
	const { t } = useTranslation();

	return (
		<>
			<h3 className={styles.title}>
				<Text tx={title} firstUpper />
			</h3>
			<FormContainer name={FieldNames.FINAL_STEP}>
				{isEntryQuestionnaire && (
					<div className={styles.formWrapper}>
						<DateField isRequired name='careDate' label='base.careDate' />
						<div>
							<TopFieldContent label='base.weekAndDay' isRequired />
							<Input disabled value={dateDifference} />
						</div>
					</div>
				)}
				<TextAreaField
					name='subjectively'
					label='base.subjectively'
					className={styles.field}
				/>
				<TextAreaField
					name='objectively'
					label='base.objectively'
					className={styles.field}
				/>
				<SelectField
					isRequired
					name='cervixScore'
					label='base.cervixScore'
					options={[
						{
							value: 'unexamined',
							label: t('base.unexamined') ?? '',
						},
						...[...Array(11).keys()].map(key => ({
							value: String(key),
							label: String(key),
						})),
					]}
					className={styles.field}
				/>
				<TextAreaField
					name='finalRecommendations'
					label='base.finalRecommendations'
					className={styles.field}
					isRequired
				/>
				{isEntryQuestionnaire && (
					<InputField
						name='identificationCode'
						label='base.identificationCode'
						disabled
						isWrapped
						isRequired
					/>
				)}
				<CalendarInputField
					name='nextExaminationDate'
					label='base.nextExaminationDate'
					patientId={patientId}
					workplaceId={workplaceId}
					className={styles.field}
					closeOnSuccess
				/>
				<DoctorSelectField
					isRequired
					name='doctor'
					label='base.doctorName'
					className={styles.field}
				/>
			</FormContainer>
		</>
	);
};
export default FinalStepFields;
